<template>
  <ReportCellWrapper
    :to="params.value.reportLink"
    :trial-over="params.value.trialOver"
  >
    <component
      :is="reportVisibility === REPORT_VISIBILITY.SHARED ? BittsTooltip : 'div'"
      data-testid="report-visibility-info"
      class="flex items-center gap-4"
    >
      <FontAwesomeIcon
        :icon="reportVizToInfo[reportVisibility].icon"
        class="text-neutral-accent h-12 w-12"
      />
      {{ reportVizToInfo[reportVisibility].text }}
      <template #title>
        <div class="flex items-center flex-wrap gap-8">
          <BittsAvatarItem
            v-for="userAccess in reportUsers"
            :key="userAccess.id"
            :user="userAccess"
            :show-initials="true"
            size="small"
            spacing="condensed"
            :title="getUserName(userAccess)"
            class="report-access-cell__user-avatar"
          />
        </div>
      </template>
    </component>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsAvatarItem, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import useAuth from '@/composables/useAuth';
import { ReportCell } from '@/interfaces/reports';
import { useTeamStore } from '@/stores';
import { ReportUserAccess } from '@/types/reports';
import { User } from '@/types/root';

const { params } = defineProps<{ params: ICellRendererParams<ReportCell> }>();

const REPORT_VISIBILITY = {
  PUBLIC: 'Public',
  PRIVATE: 'Private',
  SHARED: 'Shared',
} as const;
type ReportVisibility =
  (typeof REPORT_VISIBILITY)[keyof typeof REPORT_VISIBILITY];

const { currentOrg, currentUser } = useAuth();
const { getAuthorizationByUserId } = useTeamStore();

function getUserName(user: User): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return 'User';
}
const reportUsers = computed(() => {
  if (!params.value.report.user_access) {
    return [];
  }
  return params.value.report.user_access.map(
    (userAccess: ReportUserAccess): User => {
      if (userAccess.user_id === currentUser.value?.id)
        return currentUser.value as User;
      return getAuthorizationByUserId(userAccess.user_id)?.user as User;
    },
  );
});
const isCurrentUserInReport = computed(() => {
  if (!params.value.report.user_access) {
    return false;
  }
  return params.value.report.user_access.some(
    (userAccess: ReportUserAccess) =>
      userAccess.user_id === currentUser.value?.id,
  );
});
const reportVisibility = computed<ReportVisibility>(() => {
  if (params.value.report.visibility === 'public') {
    return REPORT_VISIBILITY.PUBLIC;
  } else if (
    params.value.report.user_access.length > 1 ||
    !isCurrentUserInReport.value
  ) {
    return REPORT_VISIBILITY.SHARED;
  } // only one user has access to this report, and it's you
  return REPORT_VISIBILITY.PRIVATE;
});

const reportVizToInfo = computed<
  Record<
    ReportVisibility,
    {
      icon: string[];
      text: string;
    }
  >
>(() => ({
  [REPORT_VISIBILITY.PUBLIC]: {
    icon: ['fak', 'company'],
    text: currentOrg.value?.name || 'Public',
  },
  [REPORT_VISIBILITY.PRIVATE]: {
    icon: ['fak', 'lock'],
    text: 'Private',
  },
  [REPORT_VISIBILITY.SHARED]: {
    icon: ['fak', 'team'],
    text: `${params.value.report.user_access.length} ${pluralize(params.value.report.user_access.length, 'Person', 'People')}`,
  },
}));
</script>
